import { Component, inject, input, OnInit, output, signal, TemplateRef, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { GoogleMap, MapMarker } from "@angular/google-maps";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { FormActionButtonsComponent, FormCheckboxComponent, FormInputComponent } from "../../../../shared/components";
import { Constants } from "../../../../shared/constants/constants";
import { AdaaBoolean, Language, PageMode } from "../../../../shared/constants/enums";
import { SmartToolTipsDirective, SmartToolTipsService } from "../../../../shared/directives";
import { EntityOfficeModelType, ValidatorModelType } from "../../../../shared/models";
import { AppService, ValidatorApiService } from "../../../../shared/services";

@Component({
  selector: "adaa-addresses-editor",
  imports: [
    FormInputComponent,
    ReactiveFormsModule,
    TranslatePipe,
    FormActionButtonsComponent,
    GoogleMap,
    MapMarker,
    FormCheckboxComponent,
    SmartToolTipsDirective,
  ],
  templateUrl: "./addresses-editor.html",
  styleUrl: "./addresses-editor.scss",
})
export class AddressesEditorComponent implements OnInit {
  private _formBuilder = inject(FormBuilder);
  private _modalService = inject(NgbModal);
  private _appService = inject(AppService);
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);
  private _smartToolTipsService = inject(SmartToolTipsService);
  private _validatorApiService = inject(ValidatorApiService);

  modal = viewChild.required<TemplateRef<unknown>>("content");

  pageMode = input.required<string>();
  currentEntityOffice = input<EntityOfficeModelType>();
  editorClosed = output<EntityOfficeModelType>();
  PageMode = PageMode;

  addressForm: FormGroup = new FormGroup({});
  options = signal<google.maps.MapOptions>({
    center: { lat: 25.276987, lng: 55.296249 },
    zoom: 5,
  });
  coordinates = signal<{ latitude: string | number; longitude: string | number }>({
    latitude: 0,
    longitude: 0,
  });
  submitted = signal<boolean>(false);
  markerOptions: google.maps.marker.AdvancedMarkerElementOptions = { gmpDraggable: false };
  markerPositions: google.maps.LatLngLiteral[] = [];
  formValidation = signal<ValidatorModelType | undefined>(undefined);

  public ngOnInit() {
    this._prepareForm();
  }

  public open() {
    if (this.pageMode() !== PageMode.view)
      this._smartToolTipsService.initSmartToolTips(Constants.SMART_TOOL_TIPS_KEY.SMART_ENTITY);

    const modal = this._modalService.open(this.modal(), {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "xl",
      centered: true,
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    modal.closed.subscribe(() => this.addressForm.reset({ isHQ: AdaaBoolean.N }));
    this.submitted.set(false);
  }

  private _prepareForm() {
    this.addressForm = this._formBuilder.group({
      addressAE: null,
      addressEN: null,
      zipCodeAE: null,
      cityAE: null,
      cityEN: null,
      geoX: null,
      geoY: null,
      isHQ: null,
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_ADDRESSES, this.addressForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.addressForm = response.form;
        },
      });
  }

  public mapClicked(event: google.maps.MapMouseEvent) {
    if (event.latLng) {
      this.markerPositions = [];
      this.markerPositions.push(event.latLng.toJSON());
      this.coordinates.set({
        latitude: event.latLng.lat().toFixed(6),
        longitude: event.latLng.lng().toFixed(6),
      });
      this.addressForm.controls["geoX"].patchValue(this.coordinates().longitude);
      this.addressForm.controls["geoY"].patchValue(this.coordinates().latitude);
    }
  }

  public submit() {
    this.submitted.set(true);
    if (this.addressForm.invalid) {
      this._toastrService.error(this._translateService.instant("common.form.label.missing_information"));
      return;
    }
    const result = {
      ...(this.pageMode() === PageMode.edit && { ...this.currentEntityOffice() }),
      ...this.addressForm.value,
      isHQ: this.addressForm.value.isHQ ?? AdaaBoolean.N,
      status: Constants.OBJECT_STATUS.ACTIVE,
    };

    this.editorClosed.emit(result);
  }

  public checkInvalid(control: string) {
    return (
      this.addressForm.controls[control].errors?.["required"] &&
      (this.addressForm.get("nameEN")?.dirty || this.submitted())
    );
  }
}
